import { call, put, takeEvery, select, all } from "redux-saga/effects";
import api from "../services/reshuffleRequests.service";
import * as reshuffleRequestsActions from "../actions/reshuffleRequests.actions";
import { generalActions } from "../actions";
import { reshuffleRequestPagination } from "../selectors/reshuffleRequests.selector";
import ACTIONS from "../constants";

const toastError = (error) => {
  const obj = JSON.parse(JSON.stringify(error));
  if (obj.status === 400 && obj.data?.errors?.[0]?.message) {
    return obj.data.errors[0].message;
  }
  return "Something went wrong. Please try again later.";
};

function* refetchList(projectId, page, size) {
  const result = yield call(api.getReshuffleRequests, { projectId, page, size });
  const pagination = JSON.parse(result.headers["x-pagination"]);
  return { data: result.data, pagination };
}

// ── Get list ──────────────────────────────────────────────────────────────────
function* getReshuffleRequests(state) {
  try {
    yield put(reshuffleRequestsActions.getReshuffleRequestsAttempt.pending);
    const result = yield call(api.getReshuffleRequests, state.payload);
    const pagination = JSON.parse(result.headers["x-pagination"]);
    yield put(reshuffleRequestsActions.getReshuffleRequestsAttempt.success({ data: result.data, pagination }));
  } catch (error) {
    if (error.message && error.message.includes("x-pagination")) {
      yield put(reshuffleRequestsActions.getReshuffleRequestsAttempt.error(""));
    } else {
      yield put(reshuffleRequestsActions.getReshuffleRequestsAttempt.error(error.message));
    }
  }
}

// ── Flag ──────────────────────────────────────────────────────────────────

function* updateReshuffleRequestFlag(state) {
  try {
    yield put(reshuffleRequestsActions.updateReshuffleRequestFlagAttempt.pending);
    yield call(api.updateReshuffleRequestFlagRequest, state.payload);
    yield put(reshuffleRequestsActions.updateReshuffleRequestFlagAttempt.success());
  } catch (error) {
    yield put(reshuffleRequestsActions.updateReshuffleRequestFlagAttempt.error(error.message));
  }
}


// ── Create ────────────────────────────────────────────────────────────────────
function* createReshuffleRequest(state) {
  try {
    yield put(reshuffleRequestsActions.createReshuffleRequestAttempt.pending);
    yield call(api.createReshuffleRequest, state.payload);
    const data = yield refetchList(state.payload.projectId, 1, state.payload.size);
    yield put(reshuffleRequestsActions.createReshuffleRequestAttempt.success(data));
    yield put(generalActions.showToast({ title: "Request created successfully", type: "success" }));
  } catch (error) {
    yield put(reshuffleRequestsActions.createReshuffleRequestAttempt.error(error.message));
    yield put(generalActions.showToast({ title: toastError(error), type: "error" }));
  }
}

// ── Update ────────────────────────────────────────────────────────────────────
function* updateReshuffleRequest(state) {
  try {
    yield put(reshuffleRequestsActions.updateReshuffleRequestAttempt.pending);
    yield call(api.updateReshuffleRequest, state.payload);
    const pagination = yield select(reshuffleRequestPagination);
    const page = pagination?.CurrentPage || 1;
    const data = yield refetchList(state.payload.projectId, page, state.payload.size);
    yield put(reshuffleRequestsActions.updateReshuffleRequestAttempt.success(data));
    yield put(generalActions.showToast({ title: "Request updated successfully", type: "success" }));
  } catch (error) {
    yield put(reshuffleRequestsActions.updateReshuffleRequestAttempt.error(error.message));
    yield put(generalActions.showToast({ title: toastError(error), type: "error" }));
  }
}

// ── Delete ────────────────────────────────────────────────────────────────────
function* deleteReshuffleRequest(state) {
  try {
    yield put(reshuffleRequestsActions.deleteReshuffleRequestAttempt.pending);
    yield call(api.deleteReshuffleRequest, state.payload);
    const pagination = yield select(reshuffleRequestPagination);
    const page = pagination?.CurrentPage || 1;
    const data = yield refetchList(state.payload.projectId, page, state.payload.size);
    yield put(reshuffleRequestsActions.deleteReshuffleRequestAttempt.success(data));
    yield put(generalActions.showToast({ title: "Request deleted successfully", type: "success" }));
  } catch (error) {
    yield put(reshuffleRequestsActions.deleteReshuffleRequestAttempt.error(error.message));
    yield put(generalActions.showToast({ title: "Something went wrong. Please try again later.", type: "error" }));
  }
}

// ── Submit ────────────────────────────────────────────────────────────────────
function* submitReshuffleRequest(state) {
  try {
    yield put(reshuffleRequestsActions.submitReshuffleRequestAttempt.pending);
    yield call(api.submitReshuffleRequest, state.payload);
    const pagination = yield select(reshuffleRequestPagination);
    const page = pagination?.CurrentPage || 1;
    const data = yield refetchList(state.payload.projectId, page, state.payload.size);
    yield put(reshuffleRequestsActions.submitReshuffleRequestAttempt.success(data));
    yield put(generalActions.showToast({ title: "Request submitted for approval", type: "success" }));
  } catch (error) {
    yield put(reshuffleRequestsActions.submitReshuffleRequestAttempt.error(error.message));
    yield put(generalActions.showToast({ title: toastError(error), type: "error" }));
  }
}

// ── Approve ───────────────────────────────────────────────────────────────────
function* approveReshuffleRequest(state) {
  try {
    yield put(reshuffleRequestsActions.approveReshuffleRequestAttempt.pending);
    yield call(api.approveReshuffleRequest, state.payload);
    const pagination = yield select(reshuffleRequestPagination);
    const page = pagination?.CurrentPage || 1;
    const data = yield refetchList(state.payload.projectId, page, state.payload.size);
    yield put(reshuffleRequestsActions.approveReshuffleRequestAttempt.success(data));
    yield put(generalActions.showToast({ title: "Request approved", type: "success" }));
  } catch (error) {
    yield put(reshuffleRequestsActions.approveReshuffleRequestAttempt.error(error.message));
    yield put(generalActions.showToast({ title: toastError(error), type: "error" }));
  }
}

// ── Reject ────────────────────────────────────────────────────────────────────
function* rejectReshuffleRequest(state) {
  try {
    yield put(reshuffleRequestsActions.rejectReshuffleRequestAttempt.pending);
    yield call(api.rejectReshuffleRequest, state.payload);
    const pagination = yield select(reshuffleRequestPagination);
    const page = pagination?.CurrentPage || 1;
    const data = yield refetchList(state.payload.projectId, page, state.payload.size);
    yield put(reshuffleRequestsActions.rejectReshuffleRequestAttempt.success(data));
    yield put(generalActions.showToast({ title: "Request rejected", type: "success" }));
  } catch (error) {
    yield put(reshuffleRequestsActions.rejectReshuffleRequestAttempt.error(error.message));
    yield put(generalActions.showToast({ title: toastError(error), type: "error" }));
  }
}

// ── Execute ───────────────────────────────────────────────────────────────────
function* executeReshuffleRequest(state) {
  try {
    yield put(reshuffleRequestsActions.executeReshuffleRequestAttempt.pending);
    yield call(api.executeReshuffleRequest, state.payload);
    const pagination = yield select(reshuffleRequestPagination);
    const page = pagination?.CurrentPage || 1;
    const data = yield refetchList(state.payload.projectId, page, state.payload.size);
    yield put(reshuffleRequestsActions.executeReshuffleRequestAttempt.success(data));
    yield put(generalActions.showToast({ title: "Request executed successfully", type: "success" }));
  } catch (error) {
    yield put(reshuffleRequestsActions.executeReshuffleRequestAttempt.error(error.message));
    yield put(generalActions.showToast({ title: toastError(error), type: "error" }));
  }
}

// ── Cancel ────────────────────────────────────────────────────────────────────
function* cancelReshuffleRequest(state) {
  try {
    yield put(reshuffleRequestsActions.cancelReshuffleRequestAttempt.pending);
    yield call(api.cancelReshuffleRequest, state.payload);
    const pagination = yield select(reshuffleRequestPagination);
    const page = pagination?.CurrentPage || 1;
    const data = yield refetchList(state.payload.projectId, page, state.payload.size);
    yield put(reshuffleRequestsActions.cancelReshuffleRequestAttempt.success(data));
    yield put(generalActions.showToast({ title: "Request cancelled", type: "success" }));
  } catch (error) {
    yield put(reshuffleRequestsActions.cancelReshuffleRequestAttempt.error(error.message));
    yield put(generalActions.showToast({ title: toastError(error), type: "error" }));
  }
}

// ── Root ──────────────────────────────────────────────────────────────────────
export default function* reshuffleRequestsSaga() {
  yield all([
    takeEvery(ACTIONS.RESHUFFLE_REQUEST_CALL, getReshuffleRequests),
    takeEvery(ACTIONS.RESHUFFLE_REQUEST_FLAG_CALL, updateReshuffleRequestFlag),
    takeEvery(ACTIONS.CREATE_RESHUFFLE_REQUEST_CALL, createReshuffleRequest),
    takeEvery(ACTIONS.UPDATE_RESHUFFLE_REQUEST_CALL, updateReshuffleRequest),
    takeEvery(ACTIONS.DELETE_RESHUFFLE_REQUEST_CALL, deleteReshuffleRequest),
    takeEvery(ACTIONS.SUBMIT_RESHUFFLE_REQUEST_CALL, submitReshuffleRequest),
    takeEvery(ACTIONS.APPROVE_RESHUFFLE_REQUEST_CALL, approveReshuffleRequest),
    takeEvery(ACTIONS.REJECT_RESHUFFLE_REQUEST_CALL, rejectReshuffleRequest),
    takeEvery(ACTIONS.EXECUTE_RESHUFFLE_REQUEST_CALL, executeReshuffleRequest),
    takeEvery(ACTIONS.CANCEL_RESHUFFLE_REQUEST_CALL, cancelReshuffleRequest),
  ]);
}
